import { motion } from 'framer-motion';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const PreviousBooksCarouselView = () => {
  const [livros, setLivros] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/livros`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        // Pega os últimos 12 livros (excluindo o destaque atual)
        setLivros(data.slice(0, -1).reverse().slice(0, 12));
      })
      .catch(() => {});
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (!livros.length) return null;

  return (
    <section className="relative w-full py-16 md:py-24 overflow-hidden bg-gradient-to-b from-white to-rose-50/20">
      <div className="container mx-auto px-6 md:px-8 relative z-10 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-2">
            LEMOS JUNTAS
          </h3>
          <p className="text-slate-500 text-sm md:text-base font-medium">
            Explore as leituras anteriores do Lily Book Club e reviva os debates
          </p>
        </motion.div>

        {/* Carrossel */}
        <div className="relative group">
          {/* Container com scroll */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 scroll-smooth"
            style={{
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {livros.map((livro, index) => (
              <motion.div
                key={livro.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex-shrink-0 group/card"
              >
                <div className="relative w-40 md:w-48 overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group-hover/card:-translate-y-3">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40 z-10 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" />

                  {/* Imagem */}
                  <div className="aspect-[3/4] bg-gradient-to-br from-rose-50 to-rose-100 overflow-hidden">
                    {livro.coverBase64 ? (
                      <motion.img
                        src={livro.coverBase64}
                        className="w-full h-full object-cover"
                        alt={livro.title}
                        whileHover={{ scale: 1.12 }}
                        transition={{ duration: 0.5 }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen size={40} className="text-rose-300" />
                      </div>
                    )}
                  </div>

                  {/* Info ao Hover */}
                  <div className="absolute inset-0 z-20 flex flex-col justify-end p-4 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300">
                    <div className="text-white">
                      <p className="font-black text-sm leading-tight mb-1">
                        {livro.title.substring(0, 30)}
                      </p>
                      <p className="text-xs font-medium text-white/80">
                        {livro.author}
                      </p>
                    </div>
                  </div>

                  {/* Badge Month */}
                  {livro.mesLabel && (
                    <div className="absolute top-3 right-3 z-20 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 text-xs font-bold text-slate-900">
                      {livro.mesLabel.split(' ')[0]}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Botões de Navegação */}
          <button
            onClick={() => scroll('left')}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-30 bg-white rounded-full p-3 shadow-lg hover:shadow-xl hover:bg-rose-50 transition-all duration-300 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0"
          >
            <ChevronLeft size={20} className="text-slate-900" />
          </button>

          <button
            onClick={() => scroll('right')}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-30 bg-white rounded-full p-3 shadow-lg hover:shadow-xl hover:bg-rose-50 transition-all duration-300 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0"
          >
            <ChevronRight size={20} className="text-slate-900" />
          </button>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-600 font-medium mb-4">
            Quer explorar mais leituras? Confira nossa biblioteca completa
          </p>
          <a
            href="/library"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-rose-300 rounded-xl font-bold text-sm text-slate-900 hover:bg-rose-50 hover:border-rose-500 transition-all duration-300"
          >
            Ver Biblioteca
            <ChevronRight size={16} />
          </a>
        </motion.div>
      </div>

      <style>{`
        [style*="overflow-x: auto"]::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default PreviousBooksCarouselView;
